import { useState } from 'react'
import { useQuery } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { usersAPI } from '../services/usersAPI'
import { Search, Plus, Filter, MoreVertical, Edit, Trash2, UserCheck, UserX } from 'lucide-react'

export default function Users() {
  const { user, hasPermission, isControlCenter } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [userType, setUserType] = useState('')
  const [page, setPage] = useState(1)

  const { data: usersData, isLoading, refetch } = useQuery(
    ['users', page, userType],
    () => usersAPI.getAll({ page, limit: 10, user_type: userType }),
    { 
      enabled: isControlCenter(),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnMount: false,
    }
  )

  const users = usersData?.data?.data?.users || []
  const pagination = usersData?.data?.data?.pagination || {}
  
  // Debug logging
  console.log('Users - API Response:', usersData)
  console.log('Users - Users Array:', users)
  console.log('Users - Pagination:', pagination)

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    // Implement search logic
  }

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value)
    setPage(1)
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getRoleBadge = (role, userType) => {
    const roleColors = {
      'Super Admin': 'bg-purple-100 text-purple-800',
      'Admin': 'bg-blue-100 text-blue-800',
      'Manager': 'bg-green-100 text-green-800',
      'Organization Admin': 'bg-orange-100 text-orange-800',
      'Organization Manager': 'bg-yellow-100 text-yellow-800',
      'Organization User': 'bg-gray-100 text-gray-800'
    }
    
    const color = roleColors[role] || 'bg-gray-100 text-gray-800'
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {role}
      </span>
    )
  }

  if (!isControlCenter()) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
        <p className="text-secondary-600">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Users</h1>
          <p className="text-secondary-600 mt-1">Manage system users and their permissions</p>
        </div>
        {hasPermission('users.create') && (
          <Button className="mt-4 sm:mt-0">
            <Plus className="h-4 w-4 mr-2" />
            Create User
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={userType}
                onChange={handleUserTypeChange}
                className="input w-full"
              >
                <option value="">All User Types</option>
                <option value="control_center">Control Center</option>
                <option value="organization">Organization</option>
              </select>
            </div>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({pagination.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">User</th>
                    <th className="table-head">Email</th>
                    <th className="table-head">Role</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Created</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {users.map((user) => (
                    <tr key={user.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {user.full_name?.charAt(0) || user.username?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-secondary-900">
                              {user.full_name || user.username}
                            </p>
                            <p className="text-sm text-secondary-500">
                              @{user.username}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <p className="text-sm text-secondary-900">{user.email}</p>
                      </td>
                      <td className="table-cell">
                        {getRoleBadge(user.role, user.user_type)}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.user_type === 'control_center' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.user_type === 'control_center' ? 'Control Center' : 'Organization'}
                        </span>
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="table-cell">
                        <p className="text-sm text-secondary-900">
                          {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          {hasPermission('users.update') && (
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {hasPermission('users.delete') && (
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-secondary-500">No users found</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-secondary-700">
                Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} results
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
